import React, { useEffect, useCallback, useState } from 'react';
import { PolygonGeometry } from '@ais-ui/api-spec/portserver';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ais-ui/ui-core';
import { UseFormReturn } from 'react-hook-form';
import { polygonSchema, createGeoJsonFromGeometry } from '@ais-ui/utils';
import { z } from 'zod';
import { useLocalization } from '@ais-ui/locale-management';
import { TERMINAL_TYPES, InnerAreaFormValues } from './constants';
import { DrawPolygonField } from '../Shapes/DrawFormFields';
import { booleanContains, polygon as turfPolygon } from '@turf/turf';

// Use the same polygon type as the form schema
type PolygonType = z.infer<typeof polygonSchema>;

interface InnerAreaFormProps {
  /** Outer port polygon for reference layer */
  outerPolygon?: PolygonGeometry;
  /** Other inner areas for reference layers */
  otherInnerAreas?: Array<{ id?: number; polygon?: PolygonGeometry }>;
  onSave: (values: InnerAreaFormValues) => void;
  onCancel: () => void;
  isEditing?: boolean;
  /** Form from parent - persists across dialog hide/show */
  form: UseFormReturn<InnerAreaFormValues>;
  /** Start drawing function from parent - handles zoom and reference layers */
  onStartDrawing: (
    type: 'Polygon' | 'Point',
    existingShape: GeoJson | null,
  ) => void;
}

export const InnerAreaForm = ({
  outerPolygon,
  otherInnerAreas,
  onSave,
  onCancel,
  isEditing = false,
  form,
  onStartDrawing,
}: InnerAreaFormProps) => {
  const { messages } = useLocalization();
  const [containmentError, setContainmentError] = useState<string | null>(null);

  const polygon = form.watch('polygon') as PolygonType | null;

  // Check if inner polygon is inside outer polygon
  const isPolygonInsideOuter = useCallback(
    (innerPoly: PolygonType | null): boolean => {
      if (!innerPoly?.coordinates || !outerPolygon?.coordinates) return false;
      try {
        const outer = turfPolygon(outerPolygon.coordinates as number[][][]);
        const inner = turfPolygon(innerPoly.coordinates as number[][][]);
        return booleanContains(outer, inner);
      } catch {
        return false;
      }
    },
    [outerPolygon],
  );

  // Validate containment when polygon changes
  useEffect(() => {
    if (polygon && outerPolygon) {
      if (!isPolygonInsideOuter(polygon)) {
        setContainmentError(
          'Inner area must be completely inside the port boundary',
        );
      } else {
        setContainmentError(null);
      }
    } else {
      setContainmentError(null);
    }
  }, [polygon, outerPolygon, isPolygonInsideOuter]);

  const handleStartDrawing = () => {
    const shape = polygon ? createGeoJsonFromGeometry(polygon) : null;
    // onStartDrawing now handles reference layers and zoom at parent level
    onStartDrawing('Polygon', shape);
  };

  const handleSubmit = (values: InnerAreaFormValues) => {
    // Final validation before save
    if (!isPolygonInsideOuter(values.polygon)) {
      setContainmentError(
        'Inner area must be completely inside the port boundary',
      );
      return;
    }
    onSave(values);
  };

  return (
    <div className="border rounded-md p-3 space-y-3 bg-muted/30">
      <div className="text-sm font-medium">
        {isEditing ? 'Edit Inner Area' : 'Add Inner Area'}
      </div>

      <div className="space-y-2">
        <Label htmlFor="innerAreaName">Name</Label>
        <Input
          id="innerAreaName"
          placeholder="Terminal name"
          {...form.register('name')}
        />
        {form.formState.errors.name && (
          <p className="text-xs text-destructive">
            {form.formState.errors.name.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="terminalType">Terminal Type</Label>
        <Select
          value={form.watch('terminalType') || 'Undefined'}
          onValueChange={(value) => form.setValue('terminalType', value)}
        >
          <SelectTrigger id="terminalType">
            <SelectValue placeholder="Select type" />
          </SelectTrigger>
          <SelectContent>
            {TERMINAL_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Area Polygon</Label>
        <DrawPolygonField
          form={form}
          geometry={polygon}
          onStartDrawing={handleStartDrawing}
          color="#f97316"
        />
        {containmentError && (
          <p className="text-xs text-destructive">{containmentError}</p>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" size="sm" onClick={onCancel}>
          {messages.genericCancel}
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={form.handleSubmit(handleSubmit)}
          disabled={!form.watch('polygon') || !!containmentError}
        >
          {isEditing ? messages.genericSubmit : 'Add'}
        </Button>
      </div>
    </div>
  );
};
